<template>
  <div class="cover-block wrap">
    <div class="cover-block__breadcrumbs">
      <SkeletonBlock
        :is-active="isSkeletonActive"
        :width="breadcrumbsSkeletonWidth"
        :height="14"
      >
        <BreadCrumbs
          v-if="!isSkeletonActive"
          :breadcrumbs
          :is-full-breadcrumbs="fullBreadcrumbsIsShown"
        />
      </SkeletonBlock>
    </div>

    <Text
      as="h1"
      :text="title"
      :skeleton="isSkeletonActive"
      class="cover-block__title"
      data-test="listing-name"
    >
      <template #skeleton>
        <SkeletonText :breakpoints-width="{ default: '680px', tablet: '360px', mobile: '320px' }" />
        <SkeletonText
          v-if="isMobile"
          :breakpoints-width="'260px'"
        />
      </template>
    </Text>

    <Text
      as="span"
      :text="subtitle"
      :skeleton="isSkeletonActive"
      :skeleton-breakpoints="'308px'"
      class="cover-block__subtitle"
    />
    <ExcursionSeoMetaDatesBlock
      :title="headTitle"
      :description="citySeoDescription"
    />
  </div>
</template>

<script setup lang="ts">
  import { BreadCrumbs, generateBreadcrumbs, type GenerateBreadcrumbsParams } from '@/entities/breadCrumbs';
  import { useCitySeoInfo } from '@/entities/city/api/useCitySeoInfo.ts';
  import { computed, watch, ref, nextTick } from 'vue';
  import type { CityInfo, CityTag } from '@/shared/api/experience';
  import { useRoute } from 'vue-router';
  import { Text } from '@/shared/ui/text';
  import { useLoaderStore } from '@/shared/store/useLoaderStore';
  import { SkeletonBlock, SkeletonText } from '@experience/core-skeleton-lib';
  import { createRubricTitlesMap } from '@/entities/rubrics/utils/create-rubric-titles-map.ts';
  import { LocationType } from '@/entities/location';
  import { useHead } from '@unhead/vue';
  import { useExcursionFiltersStore } from '@/entities/filter/store/excursion';
  import { useSeoStore } from '@/shared/store/useSeoStore.ts';
  import { ExcursionSeoMetaDatesBlock } from '@/widgets/coverBlock/excursion';
  import { useExcursionListingSkeleton } from '@/shared/composables/excursion/useExcursionDelayedListingSkeleton.ts';
  import { useExcursionTagCanonicalLink } from '@/entities/tag/composables/excursion/useExcursionCanonicalLinks.ts';
  import { useScreenSize } from '@experience/shared';

  interface Props {
    currentTag: string;
    slug: string;
    page: number;
    geoObject: CityInfo;
    rubric?: string | null;
    tags: CityTag[];
  }

  const props = defineProps<Props>();

  const loaderStore = useLoaderStore();
  const filterStore = useExcursionFiltersStore();
  const seoStore = useSeoStore();
  const { isMobile, isTablet } = useScreenSize();

  const route = useRoute();

  // /experience/123
  const isExperienceDetailsPage = computed(() => /^\/experience\/\d+$/.test(route.path));

  const { isSkeletonActive } = useExcursionListingSkeleton();
  const breadcrumbsSkeletonWidth = computed(() => (isTablet.value || isMobile.value ? 64 : 128));

  const {
    citySeoData,
    isSuccess: isCitySeoLoaded,
    isFetching: isCitySeoFetching,
  } = useCitySeoInfo(
    computed(() => props.slug),
    computed(() => props.currentTag || 'all'),
    computed(() => props.page),
    computed(() => true),
  );

  const seoLoadStartTime = ref<number | null>(null);

  // Watch for location changes to reset timer
  watch(
    () => props.slug,
    () => {
      seoLoadStartTime.value = null;
    },
  );

  // Watch for tag changes to reset seo flag with timer
  watch(
    () => props.currentTag,
    (newTag, oldTag) => {
      if (oldTag !== undefined && newTag !== oldTag) {
        loaderStore.ready.seo = false;
        seoLoadStartTime.value = Date.now();

        // If data already loaded (from cache), set ready immediately
        nextTick(() => {
          if (seoIsLoaded.value && !seoIsFetching.value && !loaderStore.ready.seo) {
            handleSeoLoaded();
          }
        });
      }
    },
  );

  const seoIsLoaded = computed(() => {
    return Boolean(isCitySeoLoaded.value);
  });

  const seoIsFetching = computed(() => {
    return Boolean(isCitySeoFetching.value);
  });

  const setSeoReady = () => {
    loaderStore.ready.seo = true;
    seoLoadStartTime.value = null;
  };

  const handleSeoLoaded = () => {
    if (seoLoadStartTime.value !== null) {
      const elapsed = Date.now() - seoLoadStartTime.value;
      const minDisplayTime = 800;
      const remainingTime = Math.max(0, minDisplayTime - elapsed);

      // If data loaded instantly (from cache), don't delay
      if (elapsed < 50) {
        setSeoReady();
      } else if (remainingTime > 0) {
        setTimeout(setSeoReady, remainingTime);
      } else {
        setSeoReady();
      }
    } else {
      setSeoReady();
    }
  };

  watch(
    () => [seoIsLoaded.value, seoIsFetching.value],
    ([isLoaded, isFetching]) => {
      // Don't reset ready.seo during background refetch (when data is already loaded)
      if (isFetching && !isLoaded) {
        if (loaderStore.ready.seo) {
          loaderStore.ready.seo = false;
        }

        return;
      }

      if (isLoaded && !isFetching && !loaderStore.ready.seo) {
        handleSeoLoaded();
      }
    },
    { immediate: true },
  );

  const title = computed(() => {
    const defaultValue = isSkeletonActive.value ? '' : 'Tripster';

    return citySeoData?.value?.seo?.h1 || defaultValue;
  });
  const subtitle = computed(() => citySeoData?.value?.seo?.h2 || '');
  const headTitle = computed(() => {
    return citySeoData?.value?.seo?.title || 'Tripster';
  });

  const noindex = computed(() => citySeoData?.value?.seo?.noindex || false);

  const seoTitleWithPageNumber = (formatTitle: string, pageNumber = 1) => {
    const amountOfExpOnPage = 54;

    const lastPageNumber = filterStore.searchTotalCount
      ? Math.floor(filterStore.searchTotalCount / amountOfExpOnPage)
      : 0;

    if (formatTitle && lastPageNumber) {
      return Number(pageNumber) > 1 ? `${formatTitle} | Страница ${pageNumber} из ${lastPageNumber}` : formatTitle;
    }

    return formatTitle || '';
  };

  const seoTitle = computed(() => seoTitleWithPageNumber(toValue(headTitle.value), Number(route.query?.page || 1)));

  const citySeoDescription = computed(() => {
    return citySeoData?.value?.seo?.description || '';
  });

  const description = computed(() => {
    if (route.query?.page && Number(route.query.page) > 1) {
      return '';
    }

    return citySeoDescription.value;
  });

  watch(
    () => headTitle.value,
    (value) => (document.title = value),
    { immediate: true },
  );

  const breadcrumbs = computed(() => {
    const params: GenerateBreadcrumbsParams = {
      currentTag: props.currentTag,
      geoObject: props.geoObject,
      rubric: props.rubric ?? null,
      rubricTitlesMap: createRubricTitlesMap(props.tags),
      isExperienceDetailsPage: isExperienceDetailsPage.value,
      locationType: LocationType.CITY,
    };

    return generateBreadcrumbs(params);
  });

  const fullBreadcrumbsIsShown = computed(() => props.currentTag !== 'all');
  const canonicalLink = useExcursionTagCanonicalLink(computed(() => props.currentTag));

  useHead({
    title: () => seoTitle.value,
    link: [
      {
        rel: 'canonical',
        href: () => canonicalLink.value,
      },
    ],
    meta: () => {
      const metaArr = [
        {
          name: 'description',
          content: () => description.value,
        },
        {
          property: 'og:title',
          content: () => seoTitle.value,
        },
        {
          property: 'og:description',
          content: () => description.value,
        },
        {
          content: () => seoStore.resizeImage,
          property: 'og:image',
        },
        {
          property: 'og:url',
          content: () => canonicalLink.value,
        },
        {
          name: 'twitter:card',
          content: 'summary_large_image',
        },
        {
          name: 'twitter:image',
          content: () => seoStore.resizeImage,
        },
      ];

      if (noindex.value) {
        metaArr.push({
          name: 'robots',
          content: 'noindex, nofollow',
        });
      }

      return metaArr;
    },
  });
</script>

<style lang="scss" scoped>
  @use '@experience/design-tokens/typography' as typography;
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  .cover-block__title {
    margin-top: 8px;
    margin-bottom: 4px;
    text-wrap: balance;

    @include typography.base-1000-950-extra;

    @include adaptivity.tablet {
      @include typography.base-700-500-extra;
    }
  }

  .cover-block__subtitle {
    display: block;
    overflow: hidden;
    margin-top: 8px;
    text-overflow: ellipsis;
    hyphens: auto;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;

    @include typography.base-300-300-medium;
  }
</style>
